/**
 * CreatorEarningsPanel — read-only earnings summary + per-campaign invoice table.
 *
 * Additive-only: does not touch invoice creation, status update, or PDF code.
 * Consumed by both the creator's Dashboard and the TM Creator Workspace.
 *
 * Props:
 *   creatorId  — optional. When present, we're in TM view and hit
 *                /api/analytics/earnings?creator_id=<id>. When absent, creator
 *                views own earnings (server uses current_user id).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, IndianRupee, Wallet, Clock, CheckCircle2, Trophy, Calendar, Building2, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Existing helper style used across the codebase (en-IN grouping)
const fmtInr = (n) => {
  if (n == null || n === '') return '\u20B90';
  return `\u20B9${Number(n).toLocaleString('en-IN')}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

export const CreatorEarningsPanel = ({ creatorId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = creatorId
          ? `${API_URL}/api/analytics/earnings?creator_id=${encodeURIComponent(creatorId)}`
          : `${API_URL}/api/analytics/earnings`;
        const { data } = await axios.get(url, { withCredentials: true });
        setData(data);
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to load earnings';
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [creatorId]);

  if (loading) {
    return (
      <div className="py-8 flex items-center justify-center text-[#6B6B6B] dark:text-zinc-500" data-testid="earnings-loading">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading earnings…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6 text-sm text-red-200" data-testid="earnings-error">
        {error}
      </div>
    );
  }

  const s = data?.summary || {};
  const rows = data?.by_campaign || [];
  const isEmpty = (s.total_earned || 0) === 0 && rows.length === 0;

  return (
    <div className="space-y-3" data-testid="creator-earnings-panel">
      {/* Section heading */}
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-0.5 font-mono">
            Creator · Finance
          </div>
          <h2 className="text-lg font-bold text-[#0F0F0F] dark:text-white">Earnings</h2>
        </div>
        {s.last_payment_date && (
          <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono inline-flex items-center gap-1">
            <Calendar size={10} className="text-[#00C972]" /> Last payment · {fmtDate(s.last_payment_date)}
          </div>
        )}
      </div>

      {/* 4 summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <Card
          icon={IndianRupee}
          label="Total Earned"
          value={fmtInr(s.total_earned)}
          color="#FF451A"
          testid="earnings-total"
        />
        <Card
          icon={Clock}
          label="Pending Payment"
          value={fmtInr(s.pending)}
          color="#FFB800"
          testid="earnings-pending"
        />
        <Card
          icon={CheckCircle2}
          label="Paid Out"
          value={fmtInr(s.paid_out)}
          color="#00C972"
          testid="earnings-paid"
        />
        <Card
          icon={Trophy}
          label="Campaigns Completed"
          value={s.campaigns_completed ?? 0}
          color="#0055FF"
          testid="earnings-campaigns"
          plain
        />
      </div>

      {/* Per-campaign table */}
      {isEmpty ? (
        <div
          className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-6 text-center"
          data-testid="earnings-empty"
        >
          <Wallet className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-2" size={24} />
          <p className="text-xs text-zinc-300 mb-0.5">No earnings yet</p>
          <p className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono">Your invoice history will appear here.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[620px]" data-testid="earnings-table">
            <thead className="bg-white/[0.03] border-b border-black/[0.08] dark:border-white/5">
              <tr className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">
                <th className="text-left py-1.5 px-2.5">Campaign</th>
                <th className="text-left py-1.5 px-2">Brand</th>
                <th className="text-right py-1.5 px-2">Amount</th>
                <th className="text-left py-1.5 px-2">Status</th>
                <th className="text-left py-1.5 px-2.5">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r) => (
                <tr key={r.invoice_id} data-testid={`earnings-row-${r.invoice_id}`}>
                  <td className="py-1.5 px-2.5">
                    <div className="text-xs text-[#0F0F0F] dark:text-white font-medium">{r.campaign_name}</div>
                    {r.invoice_number && (
                      <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono inline-flex items-center gap-1 mt-0.5">
                        <FileText size={9} /> {r.invoice_number}
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 px-2">
                    <div className="text-[11px] text-zinc-300 inline-flex items-center gap-1">
                      <Building2 size={10} className="text-[#6B6B6B] dark:text-zinc-500" /> {r.brand_name || '—'}
                    </div>
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono text-xs text-[#0F0F0F] dark:text-white">
                    {fmtInr(r.amount)}
                  </td>
                  <td className="py-1.5 px-2">
                    <StatusChip status={r.status} />
                  </td>
                  <td className="py-1.5 px-2.5 text-[10px] font-mono text-[#6B6B6B] dark:text-zinc-400">
                    {r.status === 'paid' && r.paid_date ? fmtDate(r.paid_date) : fmtDate(r.invoice_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// --- Sub-components ---------------------------------------------------------
const Card = ({ icon: Icon, label, value, color, testid, plain }) => (
  <div
    className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-lg px-3 py-2"
    data-testid={testid}
  >
    <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">
      <Icon size={10} style={{ color }} /> {label}
    </div>
    <div
      className={`font-bold ${plain ? 'text-lg font-mono' : 'text-lg'}`}
      style={{ color: plain ? color : '#ffffff' }}
    >
      {value}
    </div>
  </div>
);

const StatusChip = ({ status }) => {
  const meta = status === 'paid'
    ? { label: 'Paid', color: '#00C972' }
    : { label: 'Pending', color: '#FFB800' };
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border"
      style={{
        color: meta.color,
        borderColor: `${meta.color}40`,
        backgroundColor: `${meta.color}12`,
      }}
      data-testid={`earnings-status-${status}`}
    >
      {meta.label}
    </span>
  );
};
